import { useState, useEffect } from 'react';
import TopBar from '../../../layout/TopBar';
import FeaturedCarousel from '../components/FeaturedCarousel';
import EditorChoiceGrid from '../components/EditorChoiceGrid';

interface Author {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicture: string | null;
}

interface Dossier {
    _id: string;
    title: string;
    description: string;
    slug: string;
    createdAt: string;
    updatedAt: string;
    author: Author;
    bannerUrl?: string;
    thumbnailUrl?: string;
}

const Documentation = () => {
    const [featuredDossiers, setFeaturedDossiers] = useState<Dossier[]>([]);
    const [editorChoiceDossiers, setEditorChoiceDossiers] = useState<Dossier[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [featuredResponse, editorChoiceResponse] = await Promise.all([
                    fetch(`${process.env.REACT_APP_URL_API_MACADAMIA}/dossiers/public/featured`),
                    fetch(`${process.env.REACT_APP_URL_API_MACADAMIA}/dossiers/public/editor-choice`),
                ]);

                if (featuredResponse.ok) {
                    const featuredData = await featuredResponse.json();
                    setFeaturedDossiers(featuredData);
                }

                if (editorChoiceResponse.ok) {
                    const editorChoiceData = await editorChoiceResponse.json();
                    setEditorChoiceDossiers(editorChoiceData);
                }
            } catch (err) {
                console.error('Error fetching dossiers:', err);
                setError('Une erreur est survenue lors du chargement des dossiers');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    return (
        <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500">
            <TopBar />
            <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-12">
                <div className="max-w-7xl mx-auto">
                    {/* Error message */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                            <p className="text-red-800 dark:text-red-200">{error}</p>
                        </div>
                    )}

                    {/* Featured Carousel */}
                    <div className="mb-12">
                        <FeaturedCarousel dossiers={featuredDossiers} loading={loading} />
                    </div>

                    {/* Editor's Choice Grid */}
                    <div>
                        <h2 className="text-2xl text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi mb-6">
                            Sélection de la rédaction
                        </h2>
                        <EditorChoiceGrid dossiers={editorChoiceDossiers} loading={loading} />
                    </div>

                    {/* Empty state */}
                    {!loading && featuredDossiers.length === 0 && editorChoiceDossiers.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-500 dark:text-gray-400">
                                Aucun dossier mis en avant pour le moment
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Documentation;
